// useGlobalHotkey — a document-level Cmd/Ctrl+<key> shortcut.
//
// This is the FIRST global hotkey in the codebase (verified: no `metaKey` /
// `ctrlKey` handling exists outside tests). It is kept module-local rather than
// promoted on day one, because a single consumer cannot tell us what the shared
// shape should be — whether it needs sequences, a scope/priority stack, or a
// registry that can report conflicts.
//
// TODO(@fluxora/hooks): promote on the SECOND consumer, and make the registry
//   the thing that moves, not just this listener. Two independent
//   `document`-level handlers cannot see each other, so the day a second
//   surface claims a chord there is nothing that can detect the collision —
//   which is exactly the bug class a shared hook exists to prevent. (Same
//   promotion convention as the `TODO(@fluxora/ui)` note in
//   `module/pricing/components/TariffDetailDrawer.tsx:1-14`.)

import { useEffect, useRef } from 'react';

/**
 * True when the event originated inside somewhere the user is typing.
 *
 * ─── WHY THIS EXISTS (it is not defensive tidiness) ─────────────────────────
 * On macOS, **Ctrl+K inside a text field is the Cocoa "kill to end of line"
 * binding** — a real, widely-used editing gesture that AppKit honours in every
 * text input. A naive `metaKey || ctrlKey` handler steals it from a Mac operator
 * mid-sentence and, because we also call `preventDefault()`, deletes nothing and
 * opens a panel instead. The same class of collision applies to Ctrl/Cmd chords
 * that browsers and platforms reserve inside editable regions generally.
 *
 * `closest()` rather than a `tagName` check on the target: a caret inside a
 * nested element of a `contenteditable` region reports that inner element as the
 * target, so a direct tag comparison would miss it entirely.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const entry = target.closest('input, textarea, [contenteditable]');
  if (entry === null) return false;
  // `contenteditable="false"` is an explicit opt-OUT of editing, so an element
  // carrying it is not a text-entry target.
  return entry.getAttribute('contenteditable') !== 'false';
}

interface GlobalHotkeyOptions {
  /** When false the listener is not attached at all. */
  readonly enabled: boolean;
  /** Compared case-insensitively against `event.key`. */
  readonly key: string;
  readonly onTrigger: () => void;
}

/**
 * Binds Cmd/Ctrl+`key` on `document` while `enabled`.
 *
 * Fires on either modifier so one binding covers macOS (Cmd) and
 * Windows/Linux (Ctrl) without the hook having to sniff the platform. Calls
 * `preventDefault()` only when it actually fires, so a chord we ignore (because
 * the operator is typing) keeps its native behaviour.
 */
export function useGlobalHotkey(options: GlobalHotkeyOptions): void {
  const { enabled, key, onTrigger } = options;

  // The callback is held in a ref so that an inline arrow from the caller does
  // not detach and re-attach the listener on every render of the owning
  // component. Without this, a keydown landing between the removal and the
  // re-add is dropped.
  const onTriggerRef = useRef<() => void>(onTrigger);
  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    if (!enabled) return;

    const normalisedKey = key.toLowerCase();
    const handle = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key.toLowerCase() !== normalisedKey) return;
      // Read the file-header comment on `isTextEntryTarget` before removing.
      if (isTextEntryTarget(event.target)) return;
      event.preventDefault();
      onTriggerRef.current();
    };

    document.addEventListener('keydown', handle);
    return () => {
      document.removeEventListener('keydown', handle);
    };
  }, [enabled, key]);
}
